import { useMutation } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { LayoutWithCarousel } from '@/components/Layout';
import { TextBodySmall, TextHeadlineLarge } from '@/components/text/Texts';
import routes from '@/constants/routes';
import type { LoginErrorResponse, LoginSuccessResponse } from '@/types';
import { login } from '../../engine/authentication.mutations';
import { loginInitialValues, loginSchema, type LoginFormData } from '../../engine/login.model';
import '../../authentication-screens.styles.css';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Column, Row } from '@/components/container/Container';
import IdGeneratorService from '@/services/id-generator.service';
import { LOGIN_FORM } from '@/constants/forms';
import PasswordInput from '@/components/form-inputs/PasswordInput';
import ForgotPasswordLink from '../../components/ForgotPasswordLink';
import CheckboxInput from '@/components/form-inputs/CheckboxInput';
import { ContinueWithGoogleButton, RoundedFilledButton } from '@/components/buttons/Buttons';
import { DividerWithText } from '@/components/utils/Divider';
import { apiErrors } from '@/constants/apiErrors';
import LoadingDialog from '@/components/utils/LoadingDialog';
import { useState } from 'react';
import { getGoogleOAuthUrl, checkGoogleUser, loginViaGoogle } from '../../engine/google-auth';
import { openGoogleOAuthPopup } from '../../engine/google-oauth-popup';
import { useAuthenticationStore, useSignupStore } from '@/store';
import { InputController } from '@/components/inputs/InputController';
import TextInput from '@/components/inputs/TextInput';

const LoginScreen = () => {
	const navigate = useNavigate();
	const { setAccessToken, setUser } = useAuthenticationStore();
	const setSignupUser = useSignupStore((state) => state.setUser);
	const resetSignupState = useSignupStore((state) => state.resetState);
	const [isGoogleLoading, setIsGoogleLoading] = useState(false);

	const form = useForm({
		defaultValues: loginInitialValues,
		resolver: yupResolver(loginSchema),
	});

	const { isPending, isSuccess, mutate } = useMutation({
		mutationFn: async (request: LoginFormData) => await login(request),
		onSuccess: async ({ data }: AxiosResponse<LoginSuccessResponse>) => {
			const { ProcessId } = data;
			navigate(routes['/otp'], { state: { processId: ProcessId } });
		},
		onError: (error) => {
			if (axios.isAxiosError<LoginErrorResponse>(error)) {
				const errorDetails = error.response?.data?.error_description;
				if (errorDetails == apiErrors['This username does not exist'] || errorDetails == apiErrors['User deleted']) {
					form.setError('email', { message: errorDetails });
				} else if (errorDetails == apiErrors['Password Incorrect']) {
					form.setError('password', { message: errorDetails });
				} else {
					form.setError('email', { message: error.message ?? "Unexpected login error" });
				}
			}
		},
	});

	const handleGoogleLogin = async () => {
		setIsGoogleLoading(true);
		try {
			// Step 1: Get Google OAuth URL from backend
			const oauthUrlResponse = await getGoogleOAuthUrl();
			const { GoogleOAuthUrl } = oauthUrlResponse.data;

			// Step 2: Open popup and get access token
			const accessToken = await openGoogleOAuthPopup(GoogleOAuthUrl);

			// Step 3: Check if user exists with this Google account
			const checkUserResponse = await checkGoogleUser(accessToken);

			if (!checkUserResponse.data.Success && checkUserResponse.data.ErrorMessage) {
				form.setError('email', { message: checkUserResponse.data.ErrorMessage });
				setIsGoogleLoading(false);
				return;
			}

			const userData = checkUserResponse.data;

			// Step 4: Check if user is registered in our system
			if (!userData.ExistingUserId) {
				// New user - redirect to sign-up with pre-filled data INCLUDING provider key
				setSignupUser({
					uuid: '',
					firstName: userData.FirstName || '',
					lastName: userData.LastName || '',
					email: userData.Email || '',
					mobileNumberPrefix: '+63',
					mobileNumber: '',
					username: '',
					password: '',
					oauthProvider: 'Google',
					oauthProviderKey: userData.GoogleKey || null,
				});

				// Navigate to sign-up screen
				navigate(routes['/sign-up']);
				return;
			}

			// Step 5: Existing user - proceed with login
			const loginResponse = await loginViaGoogle(accessToken, 'link');

			if (loginResponse.data.Error) {
				form.setError('email', {
					message: loginResponse.data.ErrorDescription || loginResponse.data.Error
				});
				setIsGoogleLoading(false);
				return;
			}

			// Step 6: Store authentication data and navigate to home
			if (loginResponse.data.AccessToken) {
				setAccessToken(loginResponse.data.AccessToken);
				setUser({
					email: loginResponse.data.Email || userData.Email || '',
					firstName: loginResponse.data.firstName || userData.FirstName || '',
					lastName: loginResponse.data.lastName || userData.LastName || '',
					roleId: loginResponse.data.roleId,
				});

				navigate(routes['/']);
			} else {
				form.setError('email', { message: 'Failed to receive authentication token' });
			}
		} catch (error: any) {
			console.error('Google login error:', error);
			form.setError('email', {
				message: error.message || 'Failed to login with Google. Please try again.'
			});
		} finally {
			setIsGoogleLoading(false);
		}
	};

	const isLoading = isPending || isSuccess || isGoogleLoading;

	return (
		<>
			<LoadingDialog isLoading={isLoading} />

			<LayoutWithCarousel>
				<Card className="login-screen">
					<TextHeadlineLarge className="mb-1 text-inverseSurface" text="Welcome back!" />
					<TextBodySmall className="mb-8 text-formTextGray" text="Please log into your account." />
					<form
						id={new IdGeneratorService(LOGIN_FORM).formId}
						onSubmit={form.handleSubmit((data: LoginFormData) => mutate(data))}
						noValidate
					>
						<Column className="w-full p-input-filled">
							<InputController id="email" control={form.control}>
								<TextInput
									needErrorIcon
									label="Username"
									autoComplete="email"
									containerCls="mb-4"
								/>
							</InputController>
							<PasswordInput
								className="password-form-input"
								control={form.control}
								formName={LOGIN_FORM}
								id="password"
								autoComplete="current-password"
								containerCls="mb-4"
							/>
							<ForgotPasswordLink />
							<Row className="h-12 items-center mb-9 ml-4">
								<CheckboxInput
									id="rememberMe"
									control={form.control}
									formName={LOGIN_FORM}
									text="Remember me?"
									color="text-primary"
								/>
							</Row>
							<Row className="gap-2.5 mb-6">
								<RoundedFilledButton
									id="sign-up"
									label="Sign Up"
									className="sign-up-button w-full"
									onClick={() => {
										resetSignupState();
										navigate(routes['/sign-up']);
									}}
								/>
								<RoundedFilledButton
									id="login"
									formName={LOGIN_FORM}
									className="login-button"
									disabled={!form.formState.isDirty || isLoading}
									label="Login"
									type="submit"
								/>
							</Row>
							<DividerWithText className="mb-4" text="or" />
							<ContinueWithGoogleButton
								id="continue-with-google"
								formName={LOGIN_FORM}
								onClick={handleGoogleLogin}
								disabled={isLoading}
							/>
						</Column>
					</form>

				</Card>
			</LayoutWithCarousel>
		</>
	);
};

export default LoginScreen;
